import { type Configuration, LogLevel } from "@azure/msal-browser";

import { assertNever } from "@goose/utils/assertNever";

export const DEFAULT_SCOPES = [`${process.env.MICROSOFT_CLIENT_ID}/.default`];

/**
 * Dedicated blank page used as the redirect target for MSAL's hidden-iframe
 * silent flows (ssoSilent / acquireTokenSilent). Keeping the iframe on a static
 * page — instead of loading the full SPA at "/" — makes the response-hash
 * handshake fast and reliable, which matters under Firefox Total Cookie
 * Protection / Chrome third-party cookie partitioning where the iframe is
 * already fragile. Interactive redirect login keeps using the "/" redirectUri
 * below so msal-react can process the response.
 *
 * NOTE: this URL must be registered as a SPA redirect URI in the Azure AD app
 * registration for every environment, alongside the existing "/" redirect URI.
 */
export const SILENT_REDIRECT_URI = String(
  new URL("/blank.html", window.location.href),
);

/**
 * Configuration object to be passed to MSAL instance on creation.
 * For a full list of MSAL.js configuration parameters, visit:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/configuration.md
 */
export const MSAL_CONFIG = {
  auth: {
    clientId: process.env.MICROSOFT_CLIENT_ID,
    authority: process.env.MICROSOFT_AUTHORITY,
    redirectUri: String(new URL("/", window.location.href)),
  },
  cache: {
    cacheLocation: "localStorage", // This configures where your cache will be stored
  },
  system: {
    loggerOptions: {
      loggerCallback: (level, message, containsPii) => {
        if (containsPii) {
          if (level === LogLevel.Error) {
            reportError(new Error("MSAL Error"));
          }
          return;
        }
        switch (level) {
          case LogLevel.Error:
            reportError(new Error(message));
            return;
          case LogLevel.Info:
            console.info(message);
            return;
          case LogLevel.Verbose:
            console.debug(message);
            return;
          case LogLevel.Warning:
            console.warn(message);
            return;
          case LogLevel.Trace:
            console.trace(message);
            return;
          default:
            assertNever(level);
        }
      },
    },
  },
} as const satisfies Configuration;
